/* eslint-disable import/no-unused-modules */
import { Button } from '@rocco/components/button';
import ProductRow from '../component/proposal/ProductRow';
import PaymentRow from '../component/proposal/PaymentRow';
import { ReactComponent as APLogoBlack } from '@rocco/icons/svg/logo-new-black.svg';
import type { Proposal } from '../type/types';

import { ArrowRightLine } from '@archipro-design/icons';
import type { StyleRule } from '@archipro-design/aria';
import {
    toastMessageClassName,
    toastMessageClassNames,
    pxToRem,
} from '@archipro-design/aria';
import { LoaderCircle } from 'lucide-react';
import {
    PRIMARY_BUTTON_COLOR,
    proposalQuotePaymentColumnsClass,
    proposalProductColumnsClass,
    proposalInvoicePaymentColumnsClass,
} from '../constants';
import { proposalFormatPrice } from '../util/utils';
import { useToaster } from '~/modules/root/context/ToasterContext';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ReactComponent as APLogo } from '@rocco/icons/svg/ap-logo-135x18.svg';
import { ReactComponent as PlusIcon } from '@rocco/icons/svg/plus.svg';
import { QuoteAcceptedDialog } from '../component/accept-quote-popup/QuoteAcceptedDialog';
import { useCallback, useState, useEffect, useRef } from 'react';
import { usePaymentPopup } from '../hooks/usePaymentPopup';
import { useSetupIntentPopup } from '../hooks/useSetupIntentPopup';
import { useAcceptQuote } from '../hooks/useAcceptQuote';
import { useSearchParams, useFetcher } from '@remix-run/react';
import { useAutoReload } from '~/hooks/use-auto-reload';
import type { QuoteAcceptOrDeclineMutation } from 'generated/graphql';
import { QuoteDecisionEnum } from 'generated/graphql';
import { StripePaymentPopup } from '../component/payment/StripePaymentPopup';
import { StripeSetupIntentPopup } from '../component/payment/StripeSetupIntentPopup';
import { AcceptQuotePopup } from '../component/accept-quote-popup/AcceptQuotePopup';
import { PaymentMethodTypePopup } from '../component/payment/PaymentMethodTypePopup';
import { cn } from '@rocco/index';
import { useReactToPrint } from 'react-to-print';
import { ProductSuppliersTermsOfService } from '../component/proposal/ProductSuppliersTermsOfService';
import { useAppDisplayModeContext } from '@archipro-design/aria';

dayjs.extend(timezone);
dayjs.extend(utc);

interface ProposalSectionProps {
    proposal: Proposal;
    isPreview: boolean;
}

const reactToPrintPageStyle = `
@page {
    size: A4;
    margin: 20px;
}
@media print {
    body {
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
        background: white !important;
    }
    .no-print {
        display: none !important;
    }
    .shadow-lg {
        box-shadow: none !important;
    }
    .bg-\\[\\#D7D7D7\\] {
        background: white !important;
    }
    /* Full width print layout */
    .print-content {
        width: 100% !important;
        max-width: none !important;
    }
    /* Keep header and content together */
    .proposal-header {
        break-inside: avoid;
    }
    /* Keep product rows together when possible */
    .product-row {
        break-inside: avoid;
    }
    /* Keep payment rows together when possible */
    .payment-row {
        break-inside: avoid;
    }
    /* Keep summary section together */
    .proposal-summary {
        break-inside: avoid;
    }
    /* Force new page before footer if needed */
    .proposal-footer {
        break-before: auto;
    }
    /* Ensure print-break class takes precedence for footer */
    .proposal-footer.print-break {
        break-before: always !important;
    }
}
`;

/**
 * Formats a timestamp into a readable date string in "Mon 11 Aug, 2025" format using local timezone
 * @param timestamp - Unix timestamp (seconds or milliseconds)
 * @returns Formatted date string in "Mon 11 Aug, 2025" format, or empty string if timestamp is null/undefined
 */
const formatDateWithDayName = (
    timestamp: number | null | undefined
): string => {
    if (!timestamp) {
        return '';
    }

    // Convert seconds to milliseconds if the timestamp is in seconds (less than year 2000)
    const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;

    return dayjs(timestampMs).format('ddd D MMM, YYYY');
};

const getInvoiceDateString = (proposal: Proposal): string => {
    if (proposal.startDate && proposal.endDate) {
        return (
            formatDateWithDayName(new Date(proposal.startDate).getTime()) +
            ' - ' +
            formatDateWithDayName(new Date(proposal.endDate).getTime())
        );
    }

    return formatDateWithDayName(proposal.createdAt);
};

/*
 * eg 20 Aug
 */
const formatDateDMMM = (timestamp: number | null | undefined): string => {
    if (!timestamp) {
        return '';
    }

    return dayjs(timestamp).format('D MMM');
};

const renderNote = (note: string, proposal: Proposal) => {
    return note.replace(
        '{customer.firstName}',
        proposal.customer.firstName || ''
    );
};

/**
 * Checks if the proposal data has changed compared to the previous version
 */
const hasProposalDataChanged = (
    currentProposal: Proposal,
    previousProposal: Proposal | null
): boolean => {
    if (!previousProposal) return false;

    // Compare key fields that indicate data changes
    const statusChanged = currentProposal.status !== previousProposal.status;
    const totalChanged = currentProposal.total !== previousProposal.total;
    const paymentItemsLengthChanged =
        currentProposal.paymentItems?.length !==
        previousProposal.paymentItems?.length;

    // Deep comparison of payment items
    const paymentItemsChanged =
        JSON.stringify(currentProposal.paymentItems) !==
        JSON.stringify(previousProposal.paymentItems);

    const hasChanged =
        statusChanged ||
        totalChanged ||
        paymentItemsLengthChanged ||
        paymentItemsChanged;

    return hasChanged;
};

const hasNewPaidPayment = (
    currentProposal: Proposal,
    previousProposal: Proposal | null
): boolean => {
    if (!previousProposal) return false;

    // Compare key fields that indicate data changes
    const currentPaidCount = currentProposal.paymentItems?.filter(
        (payment) => payment.status === 'FullyPaid'
    ).length;
    const previousPaidCount = previousProposal.paymentItems?.filter(
        (payment) => payment.status === 'FullyPaid'
    ).length;

    return currentPaidCount > previousPaidCount;
};

const calcLineUsed = (proposal: Proposal) => {
    let lineUsed = 56;
    proposal.productItems?.forEach((product) => {
        lineUsed += product.description.split('\n').length + 4;
    });
    lineUsed += (proposal.paymentItems?.length || 0) * 4;
    return lineUsed;
};

/**
 * Style rule to allow toast messages to expand for longer error messages
 */
const errorToastStyleRule: StyleRule = ({ theme }) => ({
    [`&.${toastMessageClassName} .${toastMessageClassNames.container}`]: {
        [theme.screen.laptop]: {
            maxWidth: pxToRem(1200), // Increased max-width for long error messages
        },
        [theme.screen.tablet]: {
            maxWidth: '90vw', // Use viewport width on tablets for responsive behavior
        },
    },
});

const ACTION_BUTTON_CLASS = 'h-12 w-full md:w-[18rem]';

const ActionList = ({
    proposal,
    handleAcceptQuote,
    isMobile: _isMobile,
    canMakeQuoteDecision,
    makeQuoteDecisionFetcher,
    handleDeclineQuote,
    hasPayButton,
    onFirstPayNowClick,
    printRef,
    handlePrint,
}: {
    proposal: Proposal;
    handleAcceptQuote: () => void;
    isMobile: boolean;
    canMakeQuoteDecision: boolean;
    makeQuoteDecisionFetcher: ReturnType<
        typeof useFetcher<QuoteAcceptOrDeclineMutation>
    >;
    handleDeclineQuote: () => void;
    hasPayButton: boolean;
    onFirstPayNowClick: () => void;
    printRef: React.RefObject<HTMLDivElement>;
    handlePrint: () => void;
}) => {
    return (
        <div className="no-print flex w-[20rem] flex-col items-center gap-3">
            {proposal.docType === 'quote' && canMakeQuoteDecision && (
                <>
                    <Button
                        className={ACTION_BUTTON_CLASS}
                        color="primary-bm"
                        size="xLarge"
                        shape="squareRounded"
                        onClick={handleAcceptQuote}
                        disabled={
                            makeQuoteDecisionFetcher.state === 'submitting'
                        }
                    >
                        <span className={TYPO_CLASS.LIGHT_ACTION_BUTTON_TEXT}>
                            {makeQuoteDecisionFetcher.state === 'submitting' ? (
                                <>
                                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                </>
                            ) : (
                                'Accept Quote'
                            )}
                        </span>
                    </Button>
                    <Button
                        className={ACTION_BUTTON_CLASS}
                        color="white"
                        size="xLarge"
                        shape="squareRounded"
                        onClick={handleDeclineQuote}
                        disabled={
                            makeQuoteDecisionFetcher.state === 'submitting'
                        }
                    >
                        <span className={TYPO_CLASS.LIGHT_ACTION_BUTTON_TEXT}>
                            {makeQuoteDecisionFetcher.state === 'submitting' ? (
                                <>
                                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                </>
                            ) : (
                                'Decline Quote'
                            )}
                        </span>
                    </Button>
                </>
            )}
            {hasPayButton && (
                <Button
                    className={ACTION_BUTTON_CLASS}
                    color="primary-bm"
                    size="xLarge"
                    shape="squareRounded"
                    onClick={onFirstPayNowClick}
                >
                    <span className={TYPO_CLASS.LIGHT_ACTION_BUTTON_TEXT}>
                        Pay Now
                    </span>
                </Button>
            )}
            <Button
                className={ACTION_BUTTON_CLASS}
                color="white"
                size="xLarge"
                shape="squareRounded"
                onClick={() => {
                    if (printRef.current) {
                        handlePrint();
                    } else {
                        console.error('Ref is not attached!');
                    }
                }}
            >
                <span className={TYPO_CLASS.LIGHT_ACTION_BUTTON_TEXT}>
                    Download PDF
                </span>
            </Button>
        </div>
    );
};

const TYPO_CLASS = {
    // weird bug, according to figma, it should include"md:font-medium", but it's not working
    HEADLINE:
        'font-display font-semibold text-[2.31rem] md:text-[4.78rem] leading-none tracking-[-0.02em] text-[#161616]',
    // weird bug, according to figma, it should include"md:font-medium", but it's not working
    SUBHEADLINE:
        'font-display font-semibold text-[1.36rem] leading-none tracking-[-0.02em] text-[#161616]',
    // desktop only, eg "C/- Vital Addition Tax Pty Ltd", "kieran@zeqiri.co.nz"
    // weird bug, according to figma, it should include"font-display", but it's not working
    ADDRESS_TEXT:
        'font-display font-semibold text-base tracking-[-0.01em] text-[#161616]',
    // eg "What this includes:", "Products", "Scheduled Payments"
    // weird bug, according to figma, it should include"md:font-medium", but it's not working
    SECTION_HEADER:
        'font-display font-semibold text-base md:text-[0.875rem] leading-none tracking-[-0.01em] text-[#929292]',
    // weird bug, according to figma, it should include"font-display", but it's not working
    INCLUDES_TEXT:
        'font-display font-semibold text-[1rem] md:text-[1.4rem] leading-none tracking-[-0.02em] text-[#161616]',
    // desktop only, eg "QTY", "UNIT PRICE", "DISCOUNTED UNIT PRICE", "TAX", "PRICE"
    COLUMN_HEADER: 'font-medium text-xs tracking-[-0.01em] text-[#9B9B9B]',
    // eg "Discounts:", "Subtotal:", "Tax:"
    // weird bug, according to figma, it should include"md:font-medium", but it's not working
    SUMMARY_TEXT:
        'font-display font-semibold text-base leading-none tracking-[-0.01em] text-[#929292]',
    // eg "Credit Card Payment", "Bank Transfer Payment", "Notes", "Payment terms"
    BANK_SECTION_HEADER: 'font-medium text-[#161616]',
    // eg Upon acceptance, Please use the invoice number
    BANK_SECTION_TEXT: 'text-[#161616]',
    BANK_DETAIL_LEFT_TEXT: 'font-medium text-[#161616]',
    BANK_DETAIL_RIGHT_TEXT: 'text-[#161616]',
    NOTES_CONTENT_TEXT:
        'font-medium text-[1.13rem] tracking-[-0.01em] text-[#141414]',
    // 'font-display font-semibold leading-none tracking-[-0.02em] text-[#161616] md:text-2xl md:tracking-[-0.01em]',
    // eg "Confirm by 20 Aug to lock in this rate."
    // weird bug, according to figma, it should include"md:font-medium", but it's not working
    DEADLINE_TEXT:
        'md:font-medium text-[0.69rem] md:text-sm text-[#6E6E6E] md:text-[#141414] ',
    // eg "Payment terms"
    // weird bug, according to figma, it should include"md:font-medium", but it's not working
    FOOTER_LINK_TEXT:
        'font-display md:font-sans font-semibold md:font-medium text-base md:text-lg leading-tight tracking-[-0.01em] md:tracking-normal text-[#161616]',
    // desktop only, eg "ArchiPro, Mon 11 Aug, 2025"
    FOOTER_DATE: 'text-[0.80rem] text-[#929292]',
    LIGHT_ACTION_BUTTON_TEXT: 'font-medium text-[1.13rem] text-[#161616]',
    // desktop only, eg "Yes, Accept Quote"
    ROUND_ACCEPT_BUTTON_TEXT:
        'font-medium text-base tracking-[0.02em] text-[#141414]',
};

/**
 * Proposal page component that displays proposal details
 */
export const ProposalSection = ({
    proposal,
    isPreview,
}: ProposalSectionProps) => {
    const { toast } = useToaster();
    const [isOkDialogOpen, setIsOkDialogOpen] = useState(false);
    const [isTermsOfServiceOpen, setIsTermsOfServiceOpen] = useState(false);
    const [isPaymentMethodTypePopupOpen, setIsPaymentMethodTypePopupOpen] =
        useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const makeQuoteDecisionFetcher = useFetcher<QuoteAcceptOrDeclineMutation>();
    const { openPopup: openPaymentPopup, closePopup } = usePaymentPopup();
    const { openPopup: openSetupPopup } = useSetupIntentPopup();
    const { openPopup: openAcceptQuotePopup } = useAcceptQuote();
    const processedFetcherDataRef = useRef<{
        success?: boolean;
        message?: string;
        error?: string;
    } | null>(null);
    const { mobile: isMobile } = useAppDisplayModeContext();
    const printRef = useRef<HTMLDivElement>(null);

    // Page break calculation for printing
    const lineUsed = calcLineUsed(proposal);
    const hasTwoPages = lineUsed > 120;

    // React-to-print hook for triggering browser print dialog
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        pageStyle: reactToPrintPageStyle,
    });

    // Auto reload hook
    const { isAutoReloading, enableAutoReload, checkForStopCondition } =
        useAutoReload<Proposal>();

    const hasPayButton =
        proposal.docType === 'invoice' &&
        (proposal.status === 'Issued' || proposal.status === 'PartialPaid');
    const needUpdatePaymentMethod =
        proposal.isSubscription && !proposal.hasStripePaymentMethod;

    // Effect to check stop condition and stop auto reload if needed
    useEffect(() => {
        checkForStopCondition(proposal);
    }, [proposal, checkForStopCondition]);

    const getFirstUnpaidPayment = useCallback(() => {
        return proposal.paymentItems?.find(
            (payment) => payment.status === 'Upcoming'
        );
    }, [proposal.paymentItems]);
    const unpaidPayment = getFirstUnpaidPayment();

    const tryOpenUpdatePaymentMethodPopup = useCallback(() => {
        const customerId = proposal?.customer?.id;
        if (customerId) {
            if (unpaidPayment) {
                openSetupPopup(
                    customerId,
                    proposal.stripeAccount,
                    {
                        amount: unpaidPayment.amount,
                        currency: unpaidPayment.currency,
                    },
                    String(unpaidPayment.id)
                );
            }
        }
    }, [
        openSetupPopup,
        unpaidPayment,
        proposal.stripeAccount,
        proposal.customer.id,
    ]);

    // call onQuoteAccepted() when
    const onQuoteMakeDecisionDone = useCallback(
        (
            decision: QuoteDecisionEnum,
            invoiceStatus: string,
            message: string
        ) => {
            if (decision === QuoteDecisionEnum.Accept) {
                if (needUpdatePaymentMethod || !proposal.isSubscription) {
                    setIsPaymentMethodTypePopupOpen(true);
                    toast(message);
                } else {
                    enableAutoReload(
                        2,
                        10,
                        hasProposalDataChanged,
                        (stopReason) => {
                            if (stopReason === 'conditionFulfil') {
                                toast(message);
                            }
                        }
                    );
                }
            } else {
                toast(message);
            }
        },
        [
            needUpdatePaymentMethod,
            proposal.isSubscription,
            toast,
            enableAutoReload,
        ]
    );

    const handleSetupSuccess = useCallback(() => {
        // Start auto reload (will reload in 5 seconds)
        enableAutoReload(5, 20, hasNewPaidPayment, (stopReason) => {
            if (stopReason === 'conditionFulfil') {
                toast('Payment completed successfully!');
            }
        });
    }, [enableAutoReload, toast]);

    const handleSetupError = useCallback((_error: string) => {}, []);

    const handlePaymentSuccess = useCallback(() => {
        // Start auto reload (will reload in 2 seconds)
        enableAutoReload(2, 10, hasNewPaidPayment, (stopReason) => {
            if (stopReason === 'conditionFulfil') {
                toast('Payment completed successfully!');
            }
        });
    }, [enableAutoReload, toast]);

    const handlePaymentError = useCallback(
        (errorMessage: string) => {
            toast(errorMessage.substring(0, 500), {
                type: 'error',
                styleRule: errorToastStyleRule,
            });
            closePopup();
        },
        [toast, closePopup]
    );

    // Handle fetcher response
    useEffect(() => {
        const data = makeQuoteDecisionFetcher.data as
            | {
                  success?: boolean;
                  message?: string;
                  error?: string;
                  proposal?: {
                      status: string;
                  };
                  decision?: QuoteDecisionEnum;
              }
            | undefined;

        // Only process if we have new data and haven't processed this specific response yet
        if (data && data !== processedFetcherDataRef.current) {
            processedFetcherDataRef.current = data;

            if (data.success) {
                onQuoteMakeDecisionDone(
                    data.decision as QuoteDecisionEnum,
                    data.proposal?.status || '',
                    data.message || 'Action completed successfully'
                );
            } else if (data.error) {
                toast(data.error, { type: 'error' });
            }
        }
    }, [makeQuoteDecisionFetcher.data, toast, onQuoteMakeDecisionDone]);

    const handleAcceptQuote = useCallback(() => {
        openAcceptQuotePopup(
            proposal.urlSegment,
            proposal.customer.fullName,
            proposal.customer.email
        );
    }, [
        openAcceptQuotePopup,
        proposal.urlSegment,
        proposal.customer.fullName,
        proposal.customer.email,
    ]);

    const acceptQuote = useCallback(
        (signatureData: {
            name: string;
            email: string;
            signatureDataUrl: string;
            signedAt: string;
            pixelCount: number;
        }) => {
            makeQuoteDecisionFetcher.submit(
                {
                    UrlSegment: proposal.urlSegment,
                    Decision: QuoteDecisionEnum.Accept,
                    FullName: signatureData?.name,
                    Email: signatureData?.email,
                    SignatureDataUrl: signatureData?.signatureDataUrl,
                },
                {
                    method: 'post',
                    action: '/remix-api/quote-accept-or-decline',
                }
            );
        },
        [makeQuoteDecisionFetcher, proposal]
    );

    const handleDeclineQuote = useCallback(() => {
        makeQuoteDecisionFetcher.submit(
            {
                UrlSegment: proposal.urlSegment,
                Decision: QuoteDecisionEnum.Decline,
            },
            {
                method: 'post',
                action: '/remix-api/quote-accept-or-decline',
            }
        );
    }, [makeQuoteDecisionFetcher, proposal]);

    const closeQuoteAcceptedDialog = useCallback(() => {
        setIsOkDialogOpen(false);
        setSearchParams((prev) => {
            prev.delete('docType');
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const onPayNowClick = useCallback(
        (paymentId: number) => {
            if (hasPayButton) {
                if (needUpdatePaymentMethod) {
                    tryOpenUpdatePaymentMethodPopup();
                } else {
                    openPaymentPopup(
                        proposal.urlSegment,
                        proposal.stripeAccount,
                        paymentId
                    );
                }
            }
        },
        [
            hasPayButton,
            needUpdatePaymentMethod,
            proposal.stripeAccount,
            proposal.urlSegment,
            openPaymentPopup,
            tryOpenUpdatePaymentMethodPopup,
        ]
    );

    const onFirstPayNowClick = useCallback(() => {
        if (unpaidPayment) {
            onPayNowClick(unpaidPayment.id);
        }
    }, [onPayNowClick, unpaidPayment]);

    // TODO: Allow accepting/declining Draft quotes until send feature is implemented
    const canMakeQuoteDecision = !!(
        proposal.docType === 'quote' &&
        (proposal.status === 'Draft' || proposal.status === 'Sent')
    );

    const calculatePaymentTotals = useCallback(() => {
        const paidPayments =
            proposal.paymentItems?.filter(
                (payment) => payment.status === 'FullyPaid'
            ) || [];
        const amountPaidToDate = paidPayments.reduce(
            (sum, payment) => sum + payment.amount,
            0
        );
        const amountRemaining = proposal.total - amountPaidToDate;

        return { amountPaidToDate, amountRemaining };
    }, [proposal.paymentItems, proposal.total]);

    const { amountPaidToDate, amountRemaining } = calculatePaymentTotals();

    return (
        <div
            className={cn(
                'flex min-h-screen justify-center px-8',
                isPreview
                    ? 'bg-[#f6f6f6] pb-24 pt-8'
                    : 'w-full bg-[#D7D7D7] md:py-8'
            )}
            style={isPreview ? { pointerEvents: 'none' } : undefined}
        >
            {!isPreview && !isMobile && <div className="w-[20rem]"></div>}
            <div className="flex flex-row">
                <div
                    ref={printRef}
                    className="print-content w-[26rem] bg-white px-4 pb-8 shadow-lg md:mt-0 md:w-[75rem] md:px-8 md:pt-8 print:p-0"
                >
                    {/* Header */}
                    <div className="proposal-header flex flex-row justify-between border-b border-gray-200">
                        <div className="flex w-[50rem] flex-col">
                            <APLogoBlack className="mt-4 h-[114px] w-[114px]" />
                            <div className="mb-8 mt-8 md:mb-24 md:mt-16">
                                <span className={TYPO_CLASS.HEADLINE}>
                                    {proposal.proposalTemplate?.headline ||
                                        'Proposal'}
                                </span>
                                <p
                                    className={cn(
                                        TYPO_CLASS.SUBHEADLINE,
                                        'mt-4'
                                    )}
                                >
                                    {proposal.proposalTemplate?.subHeadline ||
                                        ''}
                                </p>
                            </div>
                        </div>
                        {!isMobile && (
                            <div
                                className={cn(
                                    TYPO_CLASS.ADDRESS_TEXT,
                                    'flex w-[28rem] flex-col gap-1 pt-4 text-right'
                                )}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        proposal.proposalTemplate
                                            .accountDetails || '',
                                }}
                            />
                        )}
                    </div>
                    <div className="flex w-full flex-row justify-between md:w-auto">
                        {/* What this includes */}
                        <div className="flex w-full flex-col md:w-[75%]">
                            <div className="flex flex-row justify-between">
                                <span
                                    className={cn(
                                        TYPO_CLASS.SECTION_HEADER,
                                        'mt-4'
                                    )}
                                >
                                    What this includes:
                                </span>
                                {isMobile && (
                                    <span
                                        className={cn(
                                            TYPO_CLASS.SECTION_HEADER,
                                            'mt-4'
                                        )}
                                    >
                                        {/* Mon 11 Aug, 2025 */}
                                        {getInvoiceDateString(proposal)}
                                    </span>
                                )}
                            </div>
                            <div
                                className={cn(
                                    'ml-0 mt-8 space-y-2 md:ml-8',
                                    TYPO_CLASS.INCLUDES_TEXT
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: (
                                        proposal.proposalTemplate.scope ?? ''
                                    ).replace(/\n/g, '<br />'),
                                }}
                            />
                        </div>
                        {/* Client Details */}
                        {!isMobile && (
                            <div className="flex flex-col gap-1 text-right">
                                {(proposal.startDate || proposal.endDate) && (
                                    <p
                                        className={cn(
                                            TYPO_CLASS.SECTION_HEADER,
                                            'mt-4'
                                        )}
                                    >
                                        {getInvoiceDateString(proposal)}
                                    </p>
                                )}
                                {proposal.poNumber && (
                                    <p
                                        className={cn(
                                            TYPO_CLASS.SECTION_HEADER
                                        )}
                                    >
                                        PO Number: {proposal.poNumber}
                                    </p>
                                )}
                                <p
                                    className={cn(
                                        TYPO_CLASS.ADDRESS_TEXT,
                                        'mt-12'
                                    )}
                                >
                                    {proposal.proposalTemplate?.contact
                                        ?.fullName || ''}
                                </p>
                                <p className={TYPO_CLASS.ADDRESS_TEXT}>
                                    {proposal.proposalTemplate?.contact
                                        ?.email || ''}
                                </p>
                                <p className={TYPO_CLASS.ADDRESS_TEXT}>
                                    {proposal.proposalTemplate?.contact
                                        ?.phone || ''}
                                </p>
                                <p className={TYPO_CLASS.ADDRESS_TEXT}>
                                    {proposal.proposalTemplate?.contact
                                        ?.address || ''}
                                </p>
                                <p className={TYPO_CLASS.ADDRESS_TEXT}>
                                    {proposal.proposalTemplate?.contact
                                        ?.postCode || ''}{' '}
                                    {proposal.proposalTemplate?.contact?.city ||
                                        ''}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Products Table */}
                    <div className="space-y-1 py-6 pt-12">
                        {/* Header */}
                        <div
                            className={`grid ${proposalProductColumnsClass} mb-4`}
                        >
                            <div className={TYPO_CLASS.SECTION_HEADER}>
                                Products
                                {/* Workaround, trigger downlaod font Allura manually */}
                                <span
                                    style={{
                                        fontFamily: 'Allura',
                                        color: '#FFFFFF',
                                    }}
                                >
                                    .
                                </span>
                            </div>
                            {!isMobile && (
                                <>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            `print:text-[0.6rem]`
                                        )}
                                    >
                                        QTY
                                    </div>
                                    <div
                                        className={`text-[0.54rem] font-medium text-[#9B9B9B] print:text-[0.6rem]`}
                                    >
                                        UNIT PRICE
                                    </div>
                                    <div
                                        className={`text-[0.54rem] font-medium text-[#9B9B9B] print:text-[0.6rem]`}
                                    >
                                        DISCOUNTED UNIT PRICE
                                    </div>
                                    <div
                                        className={`text-[0.54rem] font-medium text-[#9B9B9B] print:text-[0.6rem]`}
                                    >
                                        TAX
                                    </div>
                                    <div
                                        className={`mr-2 text-right text-[0.54rem] font-medium text-[#9B9B9B] print:text-[0.6rem]`}
                                    >
                                        PRICE
                                    </div>
                                </>
                            )}
                        </div>

                        {proposal.productItems?.map((productItem) => (
                            <ProductRow
                                productItem={productItem}
                                currency={proposal.currency}
                                key={productItem.id}
                                isMobile={isMobile}
                            />
                        )) || []}
                    </div>

                    {/* Scheduled Payments */}
                    <div className="space-y-2 pt-4 md:space-y-1 md:pt-12">
                        {/* Header */}
                        <div
                            className={`grid ${proposal.docType === 'quote' ? proposalQuotePaymentColumnsClass : proposalInvoicePaymentColumnsClass} mb-4`}
                        >
                            <div className={TYPO_CLASS.SECTION_HEADER}>
                                Scheduled Payments
                            </div>
                            {proposal.docType === 'quote' && !isMobile && (
                                <>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            'text-center print:text-[0.6rem]'
                                        )}
                                    >
                                        STATUS
                                    </div>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            'print:text-[0.6rem]'
                                        )}
                                    >
                                        DUE
                                    </div>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            'print:text-[0.6rem]'
                                        )}
                                    >
                                        PAID AT
                                    </div>
                                    <div className=""></div>
                                </>
                            )}
                            {proposal.docType === 'invoice' && (
                                <>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            'print:text-[0.6rem]'
                                        )}
                                    >
                                        DUE
                                    </div>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            'print:text-[0.6rem]'
                                        )}
                                    >
                                        PAID AT
                                    </div>
                                    <div className=""></div>
                                    <div
                                        className={cn(
                                            TYPO_CLASS.COLUMN_HEADER,
                                            'text-center print:text-[0.6rem]'
                                        )}
                                    >
                                        STATUS
                                    </div>
                                </>
                            )}
                        </div>

                        {proposal.paymentItems?.map((paymentItem) => (
                            <PaymentRow
                                paymentItem={paymentItem}
                                currency={proposal.currency}
                                key={paymentItem.id}
                                proposal={proposal}
                                onPayNowClick={onPayNowClick}
                                hasPayButton={
                                    hasPayButton &&
                                    paymentItem.id === unpaidPayment?.id
                                }
                                isMobile={isMobile}
                            />
                        )) || []}
                    </div>

                    {/* Summary and CTA */}
                    <div
                        className={cn(
                            'proposal-summary flex pb-16 pt-8 md:pt-12',
                            isMobile ? 'flex-col' : 'flex-row justify-between'
                        )}
                    >
                        {/* Notes */}
                        <div className="flex flex-col md:w-[45rem] md:pt-8">
                            <span className={TYPO_CLASS.BANK_SECTION_HEADER}>
                                Notes
                            </span>
                            <p
                                className={cn(
                                    'mt-2 w-82 md:w-157',
                                    TYPO_CLASS.NOTES_CONTENT_TEXT
                                )}
                            >
                                {proposal.proposalTemplate?.note?.map(
                                    (note) => (
                                        <span key={note}>
                                            {renderNote(note, proposal)}
                                        </span>
                                    )
                                ) || ''}
                            </p>
                            <span
                                className={cn(
                                    TYPO_CLASS.BANK_SECTION_HEADER,
                                    'mt-8'
                                )}
                            >
                                Payment terms
                            </span>
                            <span
                                className={cn(
                                    TYPO_CLASS.BANK_SECTION_TEXT,
                                    'mt-2 max-w-[38rem]'
                                )}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        proposal.proposalTemplate
                                            .paymentTerms ?? '',
                                }}
                            />
                            <div className="mt-10 flex flex-row gap-12">
                                <div className="flex w-72 flex-col">
                                    <span
                                        className={
                                            TYPO_CLASS.BANK_SECTION_HEADER
                                        }
                                    >
                                        Credit Card Payment
                                    </span>
                                    <span
                                        className={cn(
                                            TYPO_CLASS.BANK_SECTION_TEXT,
                                            'mt-2'
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                proposal.proposalTemplate
                                                    .creditCardTerms ?? '',
                                        }}
                                    />
                                </div>
                                <div className="flex w-75 flex-col">
                                    <span
                                        className={
                                            TYPO_CLASS.BANK_SECTION_HEADER
                                        }
                                    >
                                        Bank Transfer Payment
                                    </span>
                                    <span
                                        className={cn(
                                            TYPO_CLASS.BANK_SECTION_TEXT,
                                            'mt-2'
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                proposal.proposalTemplate
                                                    .bankTransferTerms ?? '',
                                        }}
                                    />
                                </div>
                            </div>
                            <div
                                className="mt-10 flex flex-col gap-1"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        proposal.proposalTemplate.references ??
                                        '',
                                }}
                            />
                        </div>
                        {/* Total */}
                        <div
                            className={cn(
                                TYPO_CLASS.SUMMARY_TEXT,
                                'flex flex-col gap-4 md:w-[30rem]',
                                isMobile
                                    ? 'mt-8 border-t border-gray-200 pt-6'
                                    : ''
                            )}
                        >
                            {proposal.discounts > 0 && (
                                <div className="flex flex-row justify-between">
                                    <span className="pl-2">
                                        Discount Applied:
                                    </span>
                                    <span className="pr-4 text-right">
                                        {proposalFormatPrice(
                                            -proposal.discounts,
                                            proposal.currency
                                        )}
                                    </span>
                                </div>
                            )}
                            <div className="flex flex-row justify-between">
                                <span className="pl-2">Subtotal:</span>
                                <span className="pr-4 text-right">
                                    {proposalFormatPrice(
                                        proposal.subtotal,
                                        proposal.currency
                                    )}
                                </span>
                            </div>
                            <div className="flex flex-row justify-between">
                                <span className="pl-2">Tax:</span>
                                <span className="pr-4 text-right">
                                    {proposalFormatPrice(
                                        proposal.tax,
                                        proposal.currency
                                    )}
                                </span>
                            </div>
                            <div className="mt-2 flex flex-row justify-between border-t-[1px] border-black/20 pt-4">
                                <span className="pl-2">Total Amount Due:</span>
                                <span className="pr-4 text-right text-[#141414]">
                                    {proposalFormatPrice(
                                        proposal.total,
                                        proposal.currency
                                    )}
                                </span>
                            </div>
                            {amountPaidToDate > 0 && (
                                <>
                                    <div className="flex flex-row justify-between">
                                        <span className="pl-2">
                                            Amount Paid To Date:
                                        </span>
                                        <span className="pr-4 text-right text-[#141414]">
                                            {proposalFormatPrice(
                                                amountPaidToDate,
                                                proposal.currency
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex flex-row justify-between">
                                        <span className="pl-2">
                                            Amount Remaining:
                                        </span>
                                        <span className="pr-4 text-right text-[#141414]">
                                            {proposalFormatPrice(
                                                amountRemaining,
                                                proposal.currency
                                            )}
                                        </span>
                                    </div>
                                </>
                            )}
                            {isMobile && (
                                <div className="mt-2 flex flex-row justify-end">
                                    <span
                                        className={cn(
                                            TYPO_CLASS.DEADLINE_TEXT,
                                            'pr-4 text-right'
                                        )}
                                    >
                                        Confirm by{' '}
                                        {formatDateDMMM(proposal.expiryDate)} to
                                        lock in this rate.
                                        {/* 20 Aug */}
                                    </span>
                                </div>
                            )}
                            {proposal.docType === 'quote' &&
                                !isMobile &&
                                canMakeQuoteDecision && (
                                    <>
                                        <Button
                                            className={`bg-[${PRIMARY_BUTTON_COLOR}] mt-4 h-12 px-8 py-3 hover:bg-[${PRIMARY_BUTTON_COLOR}]/80 print:hidden`}
                                            onClick={handleAcceptQuote}
                                            disabled={
                                                makeQuoteDecisionFetcher.state ===
                                                'submitting'
                                            }
                                        >
                                            <span className="items-center justify-center text-base font-medium text-[#141414]">
                                                {makeQuoteDecisionFetcher.state ===
                                                'submitting' ? (
                                                    <>
                                                        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <span
                                                            className={cn(
                                                                TYPO_CLASS.ROUND_ACCEPT_BUTTON_TEXT,
                                                                'items-center justify-center font-sans'
                                                            )}
                                                        >
                                                            Yes, Accept Quote
                                                        </span>
                                                        {isMobile && (
                                                            <ArrowRightLine className="mb-1 ml-4 h-4 w-4" />
                                                        )}
                                                    </>
                                                )}
                                            </span>
                                        </Button>
                                        <div
                                            className={cn(
                                                'mt-2 flex flex-row items-center',
                                                isMobile
                                                    ? 'justify-end'
                                                    : 'justify-center'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    TYPO_CLASS.DEADLINE_TEXT,
                                                    'print:text-base'
                                                )}
                                            >
                                                Confirm by{' '}
                                                {formatDateDMMM(
                                                    proposal.expiryDate
                                                )}{' '}
                                                to lock in this rate.
                                                {/* 20 Aug */}
                                            </span>
                                        </div>
                                    </>
                                )}
                            {proposal.docType === 'invoice' && !isMobile && (
                                <div className="mt-30"></div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        className={
                            'proposal-footer flex w-full flex-col' +
                            (hasTwoPages ? '' : ' print:break-before-page')
                        }
                    >
                        {proposal.proposalTemplate.termsOfService && (
                            <>
                                <button
                                    onClick={() =>
                                        setIsTermsOfServiceOpen(!isTermsOfServiceOpen)
                                    }
                                    className="flex h-14 w-full flex-row items-center justify-between border-t border-gray-200 transition-colors hover:bg-gray-50"
                                >
                                    <div
                                        className={cn(
                                            TYPO_CLASS.FOOTER_LINK_TEXT,
                                            'text-left'
                                        )}
                                    >
                                        {proposal.proposalTemplate
                                            .termsOfServiceTitle || 'Terms of Service'}
                                    </div>
                                    <div className="pl-8 pr-4">
                                        <PlusIcon
                                            className={`h-6 w-6 fill-[#929292] transition-transform duration-200 ${
                                                isTermsOfServiceOpen ? 'rotate-45' : ''
                                            }`}
                                        />
                                    </div>
                                </button>
                                <ProductSuppliersTermsOfService
                                    currency={proposal.currency}
                                    isVisible={isTermsOfServiceOpen}
                                    lineUsed={hasTwoPages ? 50 : 0}
                                    html={
                                        proposal.proposalTemplate.termsOfService ?? ''
                                    }
                                />
                            </>
                        )}
                        {proposal.proposalTemplate?.footerLinks?.map((link) => (
                            <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                key={link.title}
                            >
                                <div className="flex h-14 w-full flex-row items-center justify-between border-t border-gray-200">
                                    <div
                                        className={TYPO_CLASS.FOOTER_LINK_TEXT}
                                    >
                                        {link.title}
                                    </div>
                                    <div className="pl-8 pr-4">
                                        <PlusIcon className="h-6 w-6 fill-[#929292]" />
                                    </div>
                                </div>
                            </a>
                        )) || []}
                        <div
                            className={cn(
                                'flex h-20 flex-row border-t border-gray-200 pt-10',
                                isMobile ? 'justify-center' : 'justify-between'
                            )}
                        >
                            {!isMobile && (
                                <div className={TYPO_CLASS.FOOTER_DATE}>
                                    ArchiPro,{' '}
                                    {formatDateWithDayName(proposal.createdAt)}
                                </div>
                            )}
                            <APLogo />
                        </div>
                        {!isPreview && isMobile && (
                            <div className="mb-4 mt-8 flex flex-col items-center gap-3">
                                <ActionList
                                    proposal={proposal}
                                    handleAcceptQuote={handleAcceptQuote}
                                    isMobile={true}
                                    canMakeQuoteDecision={canMakeQuoteDecision}
                                    makeQuoteDecisionFetcher={
                                        makeQuoteDecisionFetcher
                                    }
                                    handleDeclineQuote={handleDeclineQuote}
                                    hasPayButton={hasPayButton}
                                    onFirstPayNowClick={onFirstPayNowClick}
                                    printRef={printRef}
                                    handlePrint={handlePrint}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!isPreview && !isMobile && (
                <div className="flex flex-col items-center gap-3 md:w-[20rem]">
                    <ActionList
                        proposal={proposal}
                        handleAcceptQuote={handleAcceptQuote}
                        isMobile={false}
                        canMakeQuoteDecision={canMakeQuoteDecision}
                        makeQuoteDecisionFetcher={makeQuoteDecisionFetcher}
                        handleDeclineQuote={handleDeclineQuote}
                        hasPayButton={hasPayButton}
                        onFirstPayNowClick={onFirstPayNowClick}
                        printRef={printRef}
                        handlePrint={handlePrint}
                    />
                </div>
            )}
            <QuoteAcceptedDialog
                isOpen={isOkDialogOpen}
                closeDialog={closeQuoteAcceptedDialog}
            />
            <StripePaymentPopup
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
            />
            <StripeSetupIntentPopup
                onSetupSuccess={handleSetupSuccess}
                onSetupError={handleSetupError}
            />
            <AcceptQuotePopup
                onConfirm={acceptQuote}
                isLoading={makeQuoteDecisionFetcher.state === 'submitting'}
            />
            <PaymentMethodTypePopup
                isOpen={isPaymentMethodTypePopupOpen}
                onClose={() => setIsPaymentMethodTypePopupOpen(false)}
                onPayOnline={onFirstPayNowClick}
            />
            {/* Auto-reload loading overlay */}
            {isAutoReloading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-8 shadow-lg">
                        <LoaderCircle className="text-blue-600 h-8 w-8 animate-spin" />
                        <p className="text-lg font-medium text-gray-700">
                            Processing...
                        </p>
                        <p className="text-sm text-gray-500">
                            Please wait while the payment is being processed.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
